import { CustomTooltip } from '@AMIEWEB/Common';
import { selectAutomationStatus } from '@AMIEWEB/Order/OrderDetails/OrderPreferences/store/OrderPreference.selector';
import { IProgressStack, ProgressBar, progressState } from 'app/ComponentLibrary/PageProgress';
import React from 'react';
import { useSelector } from 'react-redux';
import { selectPlacementProgress } from 'store/redux-store/placement-details/selectors';
import { makeStyles } from 'tss-react/mui';
import WhiteAuto from 'app/assets/images/Order/WhiteAuto.svg';
import Auto from 'app/assets/images/Order/Auto.svg';
import passportAutomationIcon from 'app/assets/images/Placement/passportAutomationWhiteIcon.svg';
import ExtensionIcon from 'app/assets/images/Placement/ExtensionIcon.svg';
import { selectExtensionInfo, selectPlacementSummary } from 'store/redux-store/placement-summary/selectors';
import { Badge, Skeleton, Typography } from '@mui/material';
import { getDateRangeLabel } from 'utils/dates/dateExtraction';
import { missingField } from 'app/constants';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useIsPassportAutomationEnabled } from './helper';

const useStyles = makeStyles()({
  tooltipPlacementBottom: {
    margin: 0,
    marginTop: 10,
    position: 'relative',
    left: '-8px',
  },
  inlineSteperIcon: {
    position: 'relative',
    left: '-8px',
    top: '-2px',
    width: '20px',
    height: '20px',
  },
  inlineStepperIconWhite: {
    height: '24px',
    width: '24px',
  },
  badge: {
    maxHeight: '16px',
    minWidth: '16px',
    height: '16px',
    width: '16px',
    borderRadius: '8px',
    fontSize: '10px',
    top: '5px',
    left: '13px',
    wordBreak: 'normal',
  },
  iconDiv: {
    paddingLeft: '5px',
    position: 'relative',
    left: '3px',
    top: '-2px',
  },
  extText: {
    color: '#0F6FB9',
    fontWeight: 'bold',
  },
  submissionIcon: {
    display: 'flex',
    flexDirection: 'row',
  },
});

export const PlacementProgressBar = () => {
  const styleInstance = useStyles();
  const { categoryProgress } = useSelector(selectPlacementProgress);

  const { promiseInProgress } = usePromiseTracker({ area: 'placement-progress-call', delay: 0 });

  const displayPassportAutomation = useIsPassportAutomationEnabled();

  const progressItems = React.useMemo<IProgressStack[]>(
    () =>
      categoryProgress.map((item, index) => ({
        ...item,
        preAddOn:
          item.tag === 'Submission' ? (
            <span className={styleInstance.classes.submissionIcon}>
              {displayPassportAutomation && <PassportAutomationIcon item={item} styleInstance={styleInstance} />}
              <AutomationIcon item={item} styleInstance={styleInstance} />
            </span>
          ) : null,
        postAddOn: item.tag === 'Assignment' ? <ExtensionBadge styleInstance={styleInstance} /> : null,
      })),
    [categoryProgress, styleInstance],
  );

  return promiseInProgress ? (
    <Skeleton height={33} />
  ) : (
    <ProgressBar items={progressItems} handleItemClick={val => {}} />
  );
};

const PassportAutomationIcon = ({ item, styleInstance: { cx, classes } }) => {
  const { t } = useTranslation();
  return (
    <CustomTooltip tooltipContent={t('Automation is On')} tooltipPlacementBottom={classes.tooltipPlacementBottom}>
      <img
        src={passportAutomationIcon}
        alt="Auto"
        style={{ height: '18px', marginTop: '3px' }}
        className={cx(classes.inlineSteperIcon)}
      />
    </CustomTooltip>
  );
};

const AutomationIcon = ({ item, styleInstance: { cx, classes } }) => {
  const { t } = useTranslation();
  const isAutomationOn = useSelector(selectAutomationStatus);

  return isAutomationOn ? (
    <CustomTooltip
      tooltipContent={t('Automation is On')}
      tooltipPlacementBottom={classes.tooltipPlacementBottom}
      isInlineContent={true}
    >
      {item.state !== progressState.future ? (
        <img
          src={WhiteAuto}
          alt="White Auto"
          className={cx(classes.inlineSteperIcon, classes.inlineStepperIconWhite)}
        />
      ) : (
        <img src={Auto} alt="Auto" className={cx(classes.inlineSteperIcon)} />
      )}
    </CustomTooltip>
  ) : null;
};

const ExtensionBadge = ({ styleInstance: { classes } }) => {
  const { t } = useTranslation();
  const extensions = useSelector(selectExtensionInfo);
  const { assignmentInformation } = useSelector(selectPlacementSummary);

  return !!extensions.length ? (
    <CustomTooltip
      disableMaxWidth
      isInlineContent={true}
      tooltipPlacementBottom={classes.tooltipPlacementBottom}
      tooltipContent={
        <>
          <Typography>
            <strong>Original Assignment: </strong>
            {getDateRangeLabel(assignmentInformation.startDate, assignmentInformation.endDate)}
          </Typography>
          {extensions.map((extension, indx) => (
            <Typography key={extension.extensionStatus}>
              <span className={classes.extText}>{`${t('Extension')} ${indx + 1}: `}</span>
              <strong>{extension?.extensionStatus ? `${extension.extensionStatus} - ` : missingField}</strong>
              {getDateRangeLabel(extension.startDate, extension.endDate)}
            </Typography>
          ))}
        </>
      }
    >
      <Badge
        classes={{ badge: classes.badge }}
        className={classes.iconDiv}
        overlap="circular"
        badgeContent={extensions.length}
        color="error"
      >
        <img width={22} src={ExtensionIcon} alt={t('Extension')} />
      </Badge>
    </CustomTooltip>
  ) : null;
};
